import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFadedPlaybackVolume } from './slideshowAudioFade';
import {
    PreloadedAudioElements,
    bufferSongAudio,
    fadeOutAndReleaseBufferedAudio,
    releaseBufferedAudioExcept
} from './slideshowAudioPreloader';
import {
    buildSongPlaybackWindows,
    findActiveSongWindow,
    getSongPlaybackOffsetSeconds
} from './slideshowAudioTimeline';

/**
 * How far the audio element may drift from the playhead before we snap it back. Below this we
 * let the audio clock run freely so normal playback stays smooth; a larger gap means the user
 * seeked/scrubbed (or resumed), so we realign.
 *
 * Kept well above Safari's steady-state audio-clock lag (~0.2s behind the RAF playhead). A
 * 0.25s threshold constantly tripped there, and each snap fired seeking/seeked (audible skips)
 * that deepened the lag into a feedback loop (FOR-16438).
 */
const SYNC_THRESHOLD_SECONDS = 1;

/**
 * Smallest gain change worth writing to an element. Well below audible (0.001 is about -60dB), so
 * skipping anything smaller keeps fades smooth while avoiding a per-frame no-op write.
 */
const VOLUME_WRITE_EPSILON = 0.001;

/** Full song length in ms from a buffered element, or 0 when metadata isn't ready yet. */
function getSongDurationMs(audio: HTMLAudioElement): number {
    const { duration } = audio;

    if (!Number.isFinite(duration) || duration <= 0) {
        return 0;
    }

    return duration * 1000;
}

interface IUseSlideshowAudioParams {
    /** Sound-timeline segments; each carries an absolute slideshowSegmentStart (ms). */
    segments: SpApi.ISlideshowSongSegment[];
    /** Whether the slideshow is currently playing. */
    isPlaying: boolean;
    /** Current playhead position from the slideshow ticker (ms). */
    currentTimeMs: number;
    /** Playback volume (0–1). */
    volume: number;
    /**
     * Silences the active song without stopping it: its element keeps running against the
     * timeline, so unmuting resumes exactly where the slides are instead of restarting the song.
     * This is also what lets a slideshow that autoplays without a user gesture start its audio at
     * all — browsers block unmuted autoplay, but a muted element plays (and stays in sync) until
     * the visitor turns audio on. Defaults to false.
     */
    muted?: boolean;
    /**
     * Resolves a signed, playable stream URL for a song id. Only called for segments that don't
     * already carry an embedded `streamUrl` (see ISlideshowSongSegment) — the client-facing
     * slideshow payload embeds one per song, so this fallback is Studio-editor-only in practice.
     * Callers own how (and whether) this is authenticated. Returning null (or never resolving)
     * simply means that song stays silent.
     */
    getStreamUrl: (songId: string) => Promise<string | null>;
    /**
     * Called whenever this hook starts or stops claiming exclusive control of audio playback —
     * not on every frame, only on the true/false transition. True whenever playing with at least
     * one song segment configured, *regardless* of whether the playhead is currently within a
     * specific song's window (a gap between songs still counts, so another source stays ducked
     * for the whole slideshow, not just individual song windows). Also called with `false` on
     * unmount if it was last reported active. Callers use this to duck/mute other audio sources
     * (e.g. gallery background music) while a slideshow's own music plays — and, symmetrically,
     * to pause the slideshow itself when a competing audio source takes over instead.
     */
    onActiveChange?: (isActive: boolean) => void;
    /**
     * When set, whatever is sounding at teardown ramps down to silence over this many milliseconds
     * before being released, instead of cutting off instantly. Used by a slideshow that serves as
     * a page's own audio (`CoverSlideshowBackground`) so leaving that page hands off to the
     * gallery playlist on a fade. Defaults to 0 — an interactive viewer the visitor stopped or
     * navigated away from should stop when they said so.
     */
    fadeOutOnTeardownMs?: number;
}

export interface IUseSlideshowAudioResult {
    /**
     * True once the audio needed to start playback is buffered enough to play without an
     * immediate stall — true immediately when there are no song segments at all. Only reflects
     * the leading song on the timeline (mirrors the image preloader's initial-buffer gate), so a
     * later song's own buffering delay can't re-stall playback that has already started; it can
     * still flip back to false if the leading song itself changes (e.g. a timeline edit removes
     * or replaces it). Callers combine this with their own image-readiness gate before flipping
     * `isPlaying` true, so audio and images always start on the same frame instead of one
     * starting alone and the other joining (and re-syncing) later (FOR-16444).
     */
    isReady: boolean;
}

/**
 * Plays a slideshow's sound-timeline songs in sync with its preview/playback position.
 *
 * Songs occupy absolute windows from each segment's `slideshowSegmentStart` (see
 * `buildSongPlaybackWindows`). Each song is buffered ahead of time into its own detached,
 * muted HTMLAudioElement (see slideshowAudioPreloader), and that same element is what plays
 * when the playhead reaches the song — only one sounds at a time. Because the element that
 * buffered the bytes is the element that plays, reaching a song's window just unmutes and calls
 * play() on already-loaded audio, so it starts immediately instead of triggering a cold fetch.
 *
 * Sync is "loose": the audio clock runs on its own between frames and is only corrected when it
 * drifts past `SYNC_THRESHOLD_SECONDS` (a seek/scrub/resume) or when the playhead crosses into a
 * different song. This avoids per-frame `currentTime` writes, which would stutter the audio.
 *
 * Volume uses a short automatic fade (see slideshowAudioFade): fade-in when the song's natural
 * beginning was trimmed off, and fade-out when the natural ending was trimmed off. Gain is
 * playhead-driven so seeks stay in sync without a separate animation loop.
 */
export function useSlideshowAudio({
    segments,
    isPlaying,
    currentTimeMs,
    volume,
    muted = false,
    getStreamUrl,
    onActiveChange,
    fadeOutOnTeardownMs = 0
}: IUseSlideshowAudioParams): IUseSlideshowAudioResult {
    const windows = useMemo(() => buildSongPlaybackWindows(segments), [segments]);

    // One detached, muted audio element per song, keyed by song id. The element that buffers a
    // song's bytes is also the one that plays it, so there's no cold fetch at the boundary.
    const preloadElementsRef = useRef<PreloadedAudioElements>(new Map());
    // Resolved stream URLs, shared between buffering and playback so a song's signed URL is
    // fetched at most once.
    const streamUrlCacheRef = useRef<Map<string, string>>(new Map());
    // The song we've committed to play. Sticky across a pause so resuming stays gapless; it only
    // differs from the actually-playing element briefly while a not-yet-buffered song loads.
    const requestedSongIdRef = useRef<string | null>(null);
    const loadTokenRef = useRef(0);
    // Latest values for async continuations that resolve a frame or more later.
    const currentTimeMsRef = useRef(currentTimeMs);
    const windowsRef = useRef(windows);
    const getStreamUrlRef = useRef(getStreamUrl);
    const onActiveChangeRef = useRef(onActiveChange);
    const isActiveRef = useRef(false);
    const isPlayingRef = useRef(isPlaying);
    const volumeRef = useRef(volume);
    const mutedRef = useRef(muted);

    currentTimeMsRef.current = currentTimeMs;
    windowsRef.current = windows;
    getStreamUrlRef.current = getStreamUrl;
    onActiveChangeRef.current = onActiveChange;
    isPlayingRef.current = isPlaying;
    volumeRef.current = volume;
    mutedRef.current = muted;

    // Fire onActiveChange(false) on unmount if we were last active, so a caller ducking another
    // audio source un-ducks it. The per-song elements are torn down by the buffering effect's
    // own unmount cleanup below.
    useEffect(() => {
        return () => {
            if (isActiveRef.current) {
                isActiveRef.current = false;
                onActiveChangeRef.current?.(false);
            }
        };
    }, []);

    // Keep the currently-playing element at the requested volume. Idle elements are muted and/or
    // paused, so setting volume only needs to reach the active one; applying it to all buffered
    // elements is harmless and covers whichever one plays next.
    useEffect(() => {
        preloadElementsRef.current.forEach((audio) => {
            audio.volume = volume;
        });
    }, [volume]);

    // Songs that have buffered enough to play without an immediate stall. Marked ready by
    // `trackSongReadiness` below via 'canplaythrough', or 'error' so a single broken song can't
    // block the slideshow's start indefinitely (mirrors the image preloader's onload/onerror
    // handling). `isReady` below only consults the leading song, but every song's readiness is
    // tracked here so that decision stays correct across timeline edits.
    const [readySongIds, setReadySongIds] = useState<ReadonlySet<string>>(new Set());

    // A song's 'canplaythrough'/'error' listener can fire after this hook unmounts — e.g. a
    // detached element mid-fade in fadeOutAndReleaseBufferedAudio's teardown ramp, which
    // deliberately outlives the unmount — so markSongReady must not update state once that's
    // happened.
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const markSongReady = useCallback((songId: string) => {
        if (!isMountedRef.current) {
            return;
        }

        setReadySongIds((prev) => {
            if (prev.has(songId)) {
                return prev;
            }

            const next = new Set(prev);
            next.add(songId);
            return next;
        });
    }, []);

    // Marks `songId` ready once `audio` has enough buffered data to play through without an
    // immediate stall, or immediately if that's already true (e.g. a cached response resolves
    // synchronously).
    const trackSongReadiness = useCallback(
        (audio: HTMLAudioElement, songId: string) => {
            if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
                markSongReady(songId);
                return;
            }

            const handleReadyOrError = () => {
                audio.removeEventListener('canplaythrough', handleReadyOrError);
                audio.removeEventListener('error', handleReadyOrError);
                markSongReady(songId);
            };

            audio.addEventListener('canplaythrough', handleReadyOrError);
            audio.addEventListener('error', handleReadyOrError);
        },
        [markSongReady]
    );

    // Buffer the audio bytes for every song as soon as the songs are known — deliberately NOT
    // gated on isPlaying, mirroring the image preloader which preloads on mount. This is what
    // makes the first song start immediately: in Studio the stream URL is resolved on demand via
    // a signed media-url round-trip, so waiting until the play click to resolve it (and only then
    // fetch bytes) is the several-second delay. Warming ahead of the click lets that round-trip
    // and the initial buffering finish during the idle time before playback starts (the client
    // payload embeds the URL, which is why it already starts near-instantly). Buffering reuses the
    // exact resolved URL playback will use — a re-signed URL would waste the download.
    useEffect(() => {
        let cancelled = false;
        const elements = preloadElementsRef.current;
        // Songs still on the timeline keep their (possibly playing) element; drop only the ones
        // that were removed, so an edit doesn't tear down and re-download a persisting song.
        const desiredSongIds = new Set(windows.map((songWindow) => songWindow.songId));

        releaseBufferedAudioExcept(elements, desiredSongIds);

        // Drop readiness for songs no longer on the timeline, so a re-added song (or one that
        // never buffered) is re-evaluated instead of trusting a stale ready flag.
        setReadySongIds((prev) => {
            if (prev.size === 0) {
                return prev;
            }

            let changed = false;
            const next = new Set(prev);

            prev.forEach((songId) => {
                if (!desiredSongIds.has(songId)) {
                    next.delete(songId);
                    changed = true;
                }
            });

            return changed ? next : prev;
        });

        // Drop cached stream URLs for songs no longer on the timeline. These URLs are signed and
        // expiring (see IMediaUrl.url), so keeping them would let a later re-add of the same song
        // reuse a stale URL and fail to buffer; it also bounds cache growth across a long edit.
        Array.from(streamUrlCacheRef.current.keys()).forEach((songId) => {
            if (!desiredSongIds.has(songId)) {
                streamUrlCacheRef.current.delete(songId);
            }
        });

        // A song may sit in more than one window (placed on the timeline twice); resolve and
        // buffer each song id only once per run so we don't fire duplicate media-url fetches.
        const handledSongIds = new Set<string>();

        windows.forEach((songWindow) => {
            if (handledSongIds.has(songWindow.songId) || elements.has(songWindow.songId)) {
                return;
            }

            handledSongIds.add(songWindow.songId);

            const cachedUrl =
                songWindow.streamUrl ?? streamUrlCacheRef.current.get(songWindow.songId);

            if (cachedUrl) {
                const buffered = bufferSongAudio(elements, songWindow.songId, cachedUrl);

                if (buffered) {
                    trackSongReadiness(buffered, songWindow.songId);
                }

                return;
            }

            void getStreamUrlRef.current(songWindow.songId).then((url) => {
                if (cancelled || !url) {
                    return;
                }

                streamUrlCacheRef.current.set(songWindow.songId, url);

                // Only set volume on a newly created element — never overwrite an element that
                // playback may already be fading. Prefer playhead-driven fade gain when this
                // song is the one currently playing.
                const alreadyBuffered = elements.has(songWindow.songId);
                const buffered = bufferSongAudio(elements, songWindow.songId, url);

                if (buffered) {
                    trackSongReadiness(buffered, songWindow.songId);
                }

                if (buffered && !alreadyBuffered) {
                    const active = findActiveSongWindow(
                        windowsRef.current,
                        currentTimeMsRef.current
                    );
                    const isActivePlaying =
                        isPlayingRef.current && active?.songId === songWindow.songId;

                    buffered.volume =
                        isActivePlaying && active
                            ? getFadedPlaybackVolume({
                                  volume: volumeRef.current,
                                  currentTimeMs: currentTimeMsRef.current,
                                  window: active,
                                  songDurationMs: getSongDurationMs(buffered)
                              })
                            : volumeRef.current;
                }
            });
        });

        return () => {
            cancelled = true;
        };
    }, [windows, trackSongReadiness]);

    // Release every buffered element on unmount so detached elements holding downloaded media
    // don't leak. Runs once (no deps) so a pause/resume or song-set change never tears down the
    // whole set — that is handled incrementally by the buffering effect above. Reads the fade
    // duration from a ref for the same reason: this effect must not re-run just because the
    // duration changed, or it would tear down mid-playback.
    const fadeOutOnTeardownMsRef = useRef(fadeOutOnTeardownMs);

    fadeOutOnTeardownMsRef.current = fadeOutOnTeardownMs;

    useEffect(() => {
        const elements = preloadElementsRef.current;

        return () => {
            fadeOutAndReleaseBufferedAudio(elements, fadeOutOnTeardownMsRef.current);
        };
    }, []);

    // Keep the active song's element aligned with the playhead. Runs each frame while playing
    // (the parent already re-renders per frame for the playhead) but only issues audio commands
    // when something actually needs to change.
    useEffect(() => {
        const elements = preloadElementsRef.current;
        const active = findActiveSongWindow(windows, currentTimeMs);
        // Claims exclusivity whenever playing with any song segments at all — not just while the
        // playhead sits inside a specific song's window — so another audio source stays ducked
        // across gaps between songs too.
        const isActive = isPlaying && windows.length > 0;

        if (isActive !== isActiveRef.current) {
            isActiveRef.current = isActive;
            onActiveChangeRef.current?.(isActive);
        }

        const pauseRequested = () => {
            const requestedId = requestedSongIdRef.current;
            const requestedEl = requestedId ? elements.get(requestedId) : null;

            if (requestedEl && !requestedEl.paused) {
                requestedEl.pause();
            }
        };

        if (!isPlaying || !active) {
            pauseRequested();

            // If the requested song is still loading (its element isn't buffered yet), cancel the
            // pending load so it can't call play() after we've paused, and clear the request so
            // resume re-selects it. A buffered song is left as the request so resume stays gapless.
            if (requestedSongIdRef.current !== null && !elements.has(requestedSongIdRef.current)) {
                loadTokenRef.current += 1;
                requestedSongIdRef.current = null;
            }

            return;
        }

        // Begins (or resumes) playback of a song on its buffered element: aligns the clock,
        // unmutes, and plays. Guarded by the load token + a re-check of the active window so a
        // stale async continuation can't start the wrong song.
        const startSong = (audio: HTMLAudioElement | null, token: number) => {
            if (!audio || token !== loadTokenRef.current) {
                return;
            }

            const latestActive = findActiveSongWindow(windowsRef.current, currentTimeMsRef.current);

            if (!latestActive || latestActive.songId !== active.songId) {
                return;
            }

            audio.muted = mutedRef.current;
            audio.volume = getFadedPlaybackVolume({
                volume: volumeRef.current,
                currentTimeMs: currentTimeMsRef.current,
                window: latestActive,
                songDurationMs: getSongDurationMs(audio)
            });
            audio.currentTime = getSongPlaybackOffsetSeconds(
                latestActive,
                currentTimeMsRef.current
            );
            void audio.play().catch(() => {
                /* autoplay policy or decode error */
            });
        };

        // Crossed into a different song (or first play): select and start its element.
        if (requestedSongIdRef.current !== active.songId) {
            // Silence the previous song right away — its window no longer covers the playhead
            // (boundary crossed, or its segment was removed) — so it can't keep sounding while
            // the next song's element loads.
            pauseRequested();

            requestedSongIdRef.current = active.songId;
            const token = ++loadTokenRef.current;

            const buffered = elements.get(active.songId);

            if (buffered) {
                startSong(buffered, token);

                return;
            }

            // Not buffered yet (e.g. play clicked before the URL resolved) — resolve, buffer,
            // then start.
            const cachedUrl = active.streamUrl ?? streamUrlCacheRef.current.get(active.songId);

            if (cachedUrl) {
                startSong(bufferSongAudio(elements, active.songId, cachedUrl), token);
            } else {
                void getStreamUrlRef.current(active.songId).then((url) => {
                    if (!url) {
                        return;
                    }

                    streamUrlCacheRef.current.set(active.songId, url);
                    startSong(bufferSongAudio(elements, active.songId, url), token);
                });
            }

            return;
        }

        // Same song requested but not buffered yet — the async start above will begin playback.
        const audio = elements.get(active.songId);

        if (!audio) {
            return;
        }

        // Same song playing: realign only on a meaningful drift (seek/scrub/resume).
        // Skip while a previous snap is still seeking — Safari reports lagging currentTime
        // mid-seek and another write would just prolong the audible gap (FOR-16438).
        const desiredOffsetSeconds = getSongPlaybackOffsetSeconds(active, currentTimeMs);

        if (
            !audio.seeking &&
            Math.abs(audio.currentTime - desiredOffsetSeconds) > SYNC_THRESHOLD_SECONDS
        ) {
            audio.currentTime = desiredOffsetSeconds;
        }

        // Apply playhead-driven fade gain (fade-in when trimStartMs > 0; fade-out when trimEndMs
        // is meaningfully before the file end). Tracks volume slider changes via volumeRef / this
        // effect's deps.
        //
        // Only written when it actually changes: this runs every frame, but outside the fade
        // regions the gain is a constant, and Safari's volume setter is not free enough to spend
        // on a no-op 60 times a second.
        const nextVolume = getFadedPlaybackVolume({
            volume: volumeRef.current,
            currentTimeMs,
            window: active,
            songDurationMs: getSongDurationMs(audio)
        });

        if (Math.abs(audio.volume - nextVolume) > VOLUME_WRITE_EPSILON) {
            audio.volume = nextVolume;
        }

        // Muting/unmuting never touches the clock, the playback state or the fade gain above, so
        // the song stays on the timeline while silent — and is already at the right offset, at
        // the right point of its fade, the moment it is unmuted.
        if (audio.muted !== muted) {
            audio.muted = muted;
        }

        if (audio.paused) {
            void audio.play().catch(() => {
                /* autoplay policy or decode error */
            });
        }
    }, [windows, isPlaying, currentTimeMs, volume, muted]);

    // Only the leading song gates readiness — a silent slideshow (no windows) has nothing to
    // wait on. A later song's own buffering delay can't re-stall playback that has already
    // started, since only windows[0] is consulted; it re-evaluates (and can flip back to false)
    // only if the leading song itself changes, e.g. a timeline edit (see IUseSlideshowAudioResult).
    const isReady = windows.length === 0 || readySongIds.has(windows[0].songId);

    return { isReady };
}
